import axios from "axios";

export const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "https://shelfkey.co";

console.log("✅ API_BASE is:", API_BASE);

export const API = axios.create({
  baseURL: API_BASE,
});

/* ---------------------------
   Access token helpers
--------------------------- */
export function setAccessToken(token) {
  if (token) {
    API.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    localStorage.setItem("sk_access_token", token);
  } else {
    delete API.defaults.headers.common["Authorization"];
    localStorage.removeItem("sk_access_token");
  }
}

// Load token on app boot (if you saved one previously)
const saved = localStorage.getItem("sk_access_token");
if (saved) setAccessToken(saved);

/* ---------------------------
   Refresh (cookie-based)
--------------------------- */
let refreshingPromise = null;

export async function refreshAccessToken() {
  if (!refreshingPromise) {
    refreshingPromise = API.post("/auth/refresh", null, { withCredentials: true })
      .then((res) => {
        const token = res?.data?.access_token;
        if (token) setAccessToken(token);
        return token ?? null;
      })
      .catch(() => null)
      .finally(() => {
        refreshingPromise = null;
      });
  }
  return refreshingPromise;
}

/* ---------------------------
   Axios response interceptor
   (auto-refresh once on 401)
--------------------------- */
API.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;

    if (status === 401 && !original.__retried) {
      original.__retried = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers = {
          ...(original.headers || {}),
          Authorization: `Bearer ${newToken}`,
        };
        return API(original);
      }
    }
    return Promise.reject(error);
  }
);

/* ---------------------------
   Auth
--------------------------- */
export async function loginUser(email, password, rememberMe = false) {
  const res = await API.post(
    "/auth/login",
    { email, password, remember_me: rememberMe },
    { headers: { "Content-Type": "application/json" } }
  );
  // set bearer for subsequent requests
  if (res?.data?.access_token) setAccessToken(res.data.access_token);
  return res.data;
}

export async function registerUser(email, password) {
  const res = await API.post("/auth/register", {
    email,
    password,
    role: "user", // adjust if needed
  });
  // optional: set token on register if you want auto-login
  if (res?.data?.access_token) setAccessToken(res.data.access_token);
  return res.data;
}

export async function logoutUser() {
  try {
    // clears the HttpOnly refresh cookie server-side
    await API.post("/auth/logout", null, { withCredentials: true });
  } catch {}
  // clear bearer + localStorage
  setAccessToken(undefined);
}

/* ---------------------------
   Stripe checkout
--------------------------- */
export async function createCheckoutSession(priceId, token) {
  // If a token is passed, use it; otherwise rely on default header set by setAccessToken()
  const headers = token
    ? { Authorization: `Bearer ${token}` }
    : API.defaults.headers.common;

  const res = await API.post(
    "/api/stripe/create-checkout-session/",
    {
      price_id: priceId,
      success_url: "http://localhost:3000/dashboard",
      cancel_url: "http://localhost:3000/plans",
    },
    { headers }
  );

  if (!res.data?.checkout_url) {
    throw new Error("Failed to create checkout session");
  }
  return res.data.checkout_url;
}
